import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../ui/dialog';
import {
  Search, Send, Trash2, AlertTriangle, Eye, EyeOff, MessageSquare, Plus,
  Check, CheckCheck, ArrowLeft,
} from 'lucide-react';
import { toast } from 'sonner';
import useMessageStream from '../../hooks/useMessageStream';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ROLE_LABELS = { party_host: 'Party Host', manager: 'Manager', staff: 'Staff' };

// Endpoint + behaviour map so the exact same UI powers both the CRM
// (admin ↔ employee) and the Employee Portal (employee ↔ manager/admin).
const MODES = {
  admin: {
    mineDir: 'from_admin',
    summaryUrl: () => `${API_URL}/api/admin/employee-messages/summary`,
    conversationUrl: (id, inc) => `${API_URL}/api/admin/employee-messages/conversation?employee_id=${id}&include_deleted=${inc}`,
    sendReply: (id, body, high) => axios.post(`${API_URL}/api/admin/employee-messages/reply`, { employee_id: id, body, high_priority: high }, { withCredentials: true }),
    deleteMessage: (mid) => axios.delete(`${API_URL}/api/admin/employee-messages/${mid}`, { withCredentials: true }),
    deleteConversation: (id) => axios.delete(`${API_URL}/api/admin/employee-messages/conversation/${id}`, { withCredentials: true }),
    allowBroadcast: true,
    containerId: 'crm-messages-chat',
    emptyList: 'No employees found.',
    emptyHint: 'Use the search on the left or pick from the list.',
  },
  employee: {
    mineDir: 'from_employee',
    summaryUrl: () => `${API_URL}/api/employee/employee-messages/summary`,
    conversationUrl: (id, inc) => `${API_URL}/api/employee/employee-messages/conversation?counterpart_id=${id}&include_deleted=${inc}`,
    sendReply: (id, body, high) => axios.post(`${API_URL}/api/employee/messages/reply`, { recipient_id: id, body, high_priority: high }, { withCredentials: true }),
    deleteMessage: (mid) => axios.delete(`${API_URL}/api/employee/messages/${mid}`, { withCredentials: true }),
    deleteConversation: (id) => axios.delete(`${API_URL}/api/employee/employee-messages/conversation/${id}`, { withCredentials: true }),
    allowBroadcast: false,
    containerId: 'emp-messages-chat',
    emptyList: 'No people found.',
    emptyHint: 'Pick a teammate or manager from the list to start a conversation.',
  },
};

const dayKey = (iso) => { const d = new Date(iso); return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`; };
const dayLabel = (iso) => {
  const d = new Date(iso); const now = new Date();
  const y = new Date(); y.setDate(now.getDate() - 1);
  if (dayKey(iso) === dayKey(now.toISOString())) return 'Today';
  if (dayKey(iso) === dayKey(y.toISOString())) return 'Yesterday';
  return d.toLocaleDateString(undefined, {
    weekday: 'short', month: 'short', day: 'numeric',
    ...(d.getFullYear() !== now.getFullYear() ? { year: 'numeric' } : {}),
  });
};
const timeLabel = (iso) => new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

export const TeamChat = ({ mode = 'admin', title, subtitle, onUnread }) => {
  const cfg = MODES[mode] || MODES.admin;
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkId = cfg.allowBroadcast ? searchParams.get('employee_id') : null;

  const [summary, setSummary] = useState([]);
  const [selected, setSelected] = useState(null);
  const [conversation, setConversation] = useState([]);
  const [search, setSearch] = useState('');
  const [convSearch, setConvSearch] = useState('');
  const [showDeleted, setShowDeleted] = useState(false);
  const [reply, setReply] = useState('');
  const [highPriority, setHighPriority] = useState(false);
  const [sending, setSending] = useState(false);
  const [deleteConvOpen, setDeleteConvOpen] = useState(false);
  const [broadcastOpen, setBroadcastOpen] = useState(false);
  const [broadcastForm, setBroadcastForm] = useState({ scope: 'all', role: 'party_host', subject: '', body: '', high_priority: false });
  const [typingName, setTypingName] = useState(null);
  const [newMsgOpen, setNewMsgOpen] = useState(false);
  const [newMsgSearch, setNewMsgSearch] = useState('');

  const scrollRef = useRef(null);
  const selectedRef = useRef(null); selectedRef.current = selected;
  const selectedIdRef = useRef(null); selectedIdRef.current = selected?.user_id || null;
  const typingTimerRef = useRef(null);
  const lastTypingSentRef = useRef(0);

  const fetchSummary = useCallback(async () => {
    try {
      const r = await axios.get(cfg.summaryUrl(), { withCredentials: true });
      const rows = r.data || [];
      setSummary(rows);
      if (onUnread) onUnread(rows.reduce((s, e) => s + (e.unread || 0), 0));
    } catch { toast.error('Failed to load conversations'); }
  }, [cfg, onUnread]);

  const fetchConversation = useCallback(async (person) => {
    if (!person) return;
    try {
      const r = await axios.get(cfg.conversationUrl(person.user_id, showDeleted), { withCredentials: true });
      setConversation(r.data.messages || []);
      fetchSummary();
      setTimeout(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      }, 50);
    } catch { toast.error('Failed to load conversation'); }
  }, [cfg, showDeleted, fetchSummary]);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  // Admin deep-link ?employee_id=
  useEffect(() => {
    if (!deepLinkId || summary.length === 0) return;
    if (selected?.user_id === deepLinkId) return;
    const match = summary.find((e) => e.user_id === deepLinkId);
    if (match) setSelected(match);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLinkId, summary]);

  useEffect(() => {
    if (cfg.allowBroadcast && selected?.user_id && searchParams.get('employee_id') !== selected.user_id) {
      const next = new URLSearchParams(searchParams);
      next.set('employee_id', selected.user_id);
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  useEffect(() => { if (selected) fetchConversation(selected); }, [selected, showDeleted, fetchConversation]);

  useMessageStream(useCallback((evt) => {
    if (evt?.type === 'message_typing') {
      if (evt.from_id && evt.from_id === selectedIdRef.current) {
        setTypingName(evt.from_name || 'typing');
        clearTimeout(typingTimerRef.current);
        typingTimerRef.current = setTimeout(() => setTypingName(null), 3500);
      }
      return;
    }
    if (evt?.type === 'message_read') {
      if (evt.reader_id && evt.reader_id === selectedIdRef.current && selectedRef.current) {
        fetchConversation(selectedRef.current);
      }
      return;
    }
    // new_message (and other refresh-worthy events): refresh rail + open thread
    fetchSummary();
    const openId = selectedIdRef.current;
    if (!openId) return;
    const involvesOpen = evt.sender_id === openId || (evt.recipient_ids || []).includes(openId);
    if (involvesOpen && selectedRef.current) fetchConversation(selectedRef.current);
  }, [fetchSummary, fetchConversation]));

  const filteredSummary = useMemo(() => {
    if (!search.trim()) return summary;
    const s = search.toLowerCase();
    return summary.filter((e) =>
      (e.name || '').toLowerCase().includes(s) ||
      (e.role_label || e.employee_role || '').toLowerCase().includes(s));
  }, [summary, search]);

  const filteredConversation = useMemo(() => {
    if (!convSearch.trim()) return conversation;
    const q = convSearch.toLowerCase();
    return conversation.filter((m) =>
      (m.body || '').toLowerCase().includes(q) || (m.subject || '').toLowerCase().includes(q));
  }, [conversation, convSearch]);

  const newMsgPeople = useMemo(() => {
    const q = newMsgSearch.trim().toLowerCase();
    if (!q) return summary;
    return summary.filter((e) =>
      (e.name || '').toLowerCase().includes(q) ||
      (e.email || '').toLowerCase().includes(q) ||
      (e.role_label || e.employee_role || '').toLowerCase().includes(q));
  }, [summary, newMsgSearch]);

  const startConversation = (person) => {
    setSelected(person);
    setNewMsgOpen(false);
    setNewMsgSearch('');
  };

  const roleFor = (e) => e?.role_label || ROLE_LABELS[e?.employee_role] || e?.employee_role || '';

  const sendTypingPing = useCallback(() => {
    if (!selectedIdRef.current) return;
    const now = Date.now();
    if (now - lastTypingSentRef.current < 2500) return;
    lastTypingSentRef.current = now;
    axios.post(`${API_URL}/api/messages/typing`, { counterpart_id: selectedIdRef.current }, { withCredentials: true }).catch(() => {});
  }, []);

  const onReplyChange = (val) => { setReply(val); if (val.trim()) sendTypingPing(); };

  const sendReply = async () => {
    if (!reply.trim() || !selected) return;
    setSending(true);
    const optimistic = {
      message_id: `tmp_${Date.now()}`,
      direction: cfg.mineDir,
      sender_name: 'You',
      body: reply.trim(),
      subject: 'Reply',
      high_priority: highPriority,
      created_at: new Date().toISOString(),
      read_by: {},
      __optimistic: true,
    };
    setConversation((prev) => [...prev, optimistic]);
    const body = reply.trim();
    setReply(''); setHighPriority(false);
    setTimeout(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, 20);
    try {
      await cfg.sendReply(selected.user_id, body, optimistic.high_priority);
      await fetchConversation(selected);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send');
      setConversation((prev) => prev.filter((m) => m.message_id !== optimistic.message_id));
      setReply(body);
    } finally { setSending(false); }
  };

  const deleteMessage = async (id) => {
    if (!window.confirm('Delete this message?')) return;
    try {
      await cfg.deleteMessage(id);
      toast.success('Deleted');
      await fetchConversation(selected);
    } catch { toast.error('Failed'); }
  };

  const deleteConversation = async () => {
    if (!selected) return;
    try {
      const r = await cfg.deleteConversation(selected.user_id);
      toast.success(`Deleted ${r.data.deleted} message(s)`);
      setDeleteConvOpen(false);
      await fetchConversation(selected);
    } catch { toast.error('Failed'); }
  };

  const sendBroadcast = async () => {
    const subject = broadcastForm.subject.trim().slice(0, 100);
    const body = broadcastForm.body.trim().slice(0, 2000);
    if (!subject || !body) { toast.error('Subject + body required'); return; }
    try {
      const payload = {
        scope: broadcastForm.scope,
        employee_id: broadcastForm.scope === 'individual' ? selected?.user_id : null,
        role: broadcastForm.scope === 'role' ? broadcastForm.role : null,
        subject, body,
        high_priority: broadcastForm.high_priority,
        send_email_copy: false,
      };
      const r = await axios.post(`${API_URL}/api/admin/messages`, payload, { withCredentials: true });
      toast.success(`Sent to ${r.data.recipient_count} employee(s)`);
      setBroadcastOpen(false);
      setBroadcastForm({ scope: 'all', role: 'party_host', subject: '', body: '', high_priority: false });
      fetchSummary();
      if (selected) fetchConversation(selected);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const highlight = (text) => {
    const content = text || '';
    const applyMentions = (str) => {
      const parts = []; let i = 0;
      const re = /@(All|Everyone|Team|PartyHosts?|Hosts|Managers?|Staff|Kitchen)\b/gi;
      let match;
      while ((match = re.exec(str)) !== null) {
        if (match.index > i) parts.push(str.slice(i, match.index));
        parts.push(<span key={`m-${match.index}`} className="inline-block bg-[#FFD166]/30 text-[#B8860B] rounded px-1 font-medium">{match[0]}</span>);
        i = match.index + match[0].length;
      }
      if (i < str.length) parts.push(str.slice(i));
      return parts;
    };
    if (!convSearch.trim()) return applyMentions(content);
    const q = convSearch.trim();
    const parts = content.split(new RegExp(`(${q.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&')})`, 'ig'));
    return parts.map((p, i) => p.toLowerCase() === q.toLowerCase()
      ? <mark key={i} className="bg-yellow-200">{p}</mark>
      : <React.Fragment key={i}>{applyMentions(p)}</React.Fragment>);
  };

  let lastDay = null;

  return (
    <div className="space-y-4" data-testid={cfg.containerId}>
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-2xl sm:text-3xl font-bold text-[#1E1E24] truncate" style={{ fontFamily: 'Fredoka, sans-serif' }}>
            {title || (cfg.allowBroadcast ? 'Employee Messages' : 'Messages')}
          </h1>
          <p className="text-[#6B6B7A] text-sm truncate">
            {subtitle || (cfg.allowBroadcast ? 'Chat with your team — one-on-one or broadcast to all.' : 'Chat with your managers and the office.')}
          </p>
        </div>
        {cfg.allowBroadcast && (
          <div className="flex items-center gap-2 flex-shrink-0">
            <Button onClick={() => setNewMsgOpen(true)} variant="outline" className="rounded-full border-[#E8E5DF] text-[#1E1E24] hover:bg-[#FFF8E7]" data-testid="new-message-btn">
              <Plus className="w-4 h-4 mr-1" /> New Message
            </Button>
            <Button onClick={() => setBroadcastOpen(true)} className="bg-[#FF3B6B] hover:bg-[#E62E58] text-white rounded-full" data-testid="broadcast-btn">
              <Plus className="w-4 h-4 mr-1" /> New Broadcast
            </Button>
          </div>
        )}
        {!cfg.allowBroadcast && (
          <Button onClick={() => setNewMsgOpen(true)} className="bg-[#FF3B6B] hover:bg-[#E62E58] text-white rounded-full flex-shrink-0" data-testid="new-message-btn">
            <Plus className="w-4 h-4 mr-1" /> New Message
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[320px_1fr] gap-0 md:gap-4 h-[70vh]">
        {/* Left panel */}
        <div className={`bg-white rounded-2xl border border-[#E8E5DF] flex-col overflow-hidden ${selected ? 'hidden md:flex' : 'flex'}`}>
          <div className="p-3 border-b border-[#E8E5DF]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9a9aa6]" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search conversations…"
                className="pl-9 rounded-xl border-[#E8E5DF]" data-testid="employee-search" />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto divide-y divide-[#F0EEEA]">
            {filteredSummary.length === 0 ? (
              <p className="p-6 text-center text-[#6B6B7A] text-sm">{cfg.emptyList}</p>
            ) : filteredSummary.map((e) => (
              <button
                key={e.user_id}
                onClick={() => setSelected(e)}
                className={`w-full p-3 text-left hover:bg-[#FFFDF8] transition-colors ${selected?.user_id === e.user_id ? 'bg-[#FFF8E7]' : ''}`}
                data-testid={`emp-item-${e.user_id}`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#FF3B6B]/15 text-[#FF3B6B] flex items-center justify-center font-semibold flex-shrink-0">
                    {(e.name || 'E').slice(0, 1).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-medium text-[#1E1E24] truncate">{e.name || e.email}</p>
                      {e.unread > 0 && (
                        <span className="bg-[#FF3B6B] text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0" data-testid={`unread-${e.user_id}`}>
                          {e.unread > 9 ? '9+' : e.unread}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-[#9a9aa6]">{roleFor(e)}</p>
                    {e.last_message_preview && (
                      <p className="text-xs text-[#6B6B7A] truncate mt-0.5">
                        {e.last_message_by === 'employee' ? '↙' : '↗'} {e.last_message_preview}
                      </p>
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Right panel */}
        <div className={`bg-white rounded-2xl border border-[#E8E5DF] flex-col overflow-hidden ${selected ? 'flex' : 'hidden md:flex'}`}>
          {!selected ? (
            <div className="flex-1 flex flex-col items-center justify-center text-[#6B6B7A] p-8 text-center">
              <MessageSquare className="w-12 h-12 text-[#FF3B6B]/30 mb-3" />
              <p className="font-medium">Select a conversation to read and reply.</p>
              <p className="text-xs mt-1">{cfg.emptyHint}</p>
            </div>
          ) : (
            <>
              {/* Header */}
              <div className="p-3 border-b border-[#E8E5DF] flex items-center gap-2 sm:gap-3 bg-[#FFFDF8]">
                <button onClick={() => setSelected(null)} className="md:hidden w-8 h-8 rounded-full flex items-center justify-center hover:bg-[#F0EEEA] text-[#6B6B7A] flex-shrink-0" data-testid="conv-back-btn" title="Back">
                  <ArrowLeft className="w-4 h-4" />
                </button>
                <div className="w-10 h-10 rounded-full bg-[#FF3B6B]/15 text-[#FF3B6B] flex items-center justify-center font-semibold flex-shrink-0">
                  {(selected.name || 'E').slice(0, 1).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-[#1E1E24] truncate" style={{ fontFamily: 'Fredoka' }}>{selected.name}</p>
                  <p className="text-xs text-[#6B6B7A] truncate">{roleFor(selected)}{selected.email ? ` · ${selected.email}` : ''}</p>
                </div>
                <div className="relative hidden sm:block w-40 lg:w-56">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3 h-3 text-[#9a9aa6]" />
                  <Input value={convSearch} onChange={(e) => setConvSearch(e.target.value)} placeholder="Search…"
                    className="pl-7 h-8 text-xs rounded-xl border-[#E8E5DF]" data-testid="conv-search" />
                </div>
                <button
                  onClick={() => setShowDeleted((v) => !v)}
                  title={showDeleted ? 'Hide deleted' : 'Show deleted'}
                  className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${showDeleted ? 'bg-[#FFD166]/30 text-[#B8860B]' : 'hover:bg-[#F0EEEA] text-[#6B6B7A]'}`}
                  data-testid="toggle-show-deleted"
                >
                  {showDeleted ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                </button>
                <Button size="sm" variant="ghost" onClick={() => setDeleteConvOpen(true)} className="text-red-500 flex-shrink-0 px-2" data-testid="delete-conv-btn">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>

              {/* Messages */}
              <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 sm:px-4 py-3 space-y-2 bg-[#FAFAF7]/40">
                {filteredConversation.length === 0 ? (
                  <p className="text-center text-[#9a9aa6] text-sm py-10">No messages yet. Type below to start.</p>
                ) : filteredConversation.map((m) => {
                  const mine = m.direction === cfg.mineDir;
                  const isDeleted = !!(m.deleted || m.deleted_at);
                  const readByCounterpart = !!(m.read_by && selected && m.read_by[selected.user_id]);
                  const k = dayKey(m.created_at);
                  const showSep = k !== lastDay; lastDay = k;
                  return (
                    <React.Fragment key={m.message_id}>
                      {showSep && (
                        <div className="flex justify-center py-1.5">
                          <span className="text-[10px] font-semibold text-[#9a9aa6] bg-white border border-[#E8E5DF] rounded-full px-3 py-0.5">
                            {dayLabel(m.created_at)}
                          </span>
                        </div>
                      )}
                      <div className={`flex ${mine ? 'justify-end' : 'justify-start'} group`}>
                        <div className="max-w-[80%] sm:max-w-[70%]">
                          <div className={`rounded-2xl px-4 py-2.5 text-sm shadow-sm ${
                            mine ? 'bg-[#FF3B6B] text-white rounded-br-sm' : 'bg-white border border-[#E8E5DF] text-[#1E1E24] rounded-bl-sm'
                          } ${m.high_priority ? 'ring-2 ring-red-400' : ''} ${isDeleted ? 'opacity-50 italic' : ''}`}
                          data-testid={`msg-${m.message_id}`}>
                            {m.high_priority && (
                              <div className={`flex items-center gap-1 text-xs mb-1 ${mine ? 'text-white/90' : 'text-red-600'}`}>
                                <AlertTriangle className="w-3 h-3" /> High priority
                              </div>
                            )}
                            {m.subject && !['Message', 'Reply', 'Employee message'].includes(m.subject) && (
                              <p className={`font-semibold text-xs mb-1 ${mine ? 'text-white/95' : 'text-[#1E1E24]'}`}>{highlight(m.subject)}</p>
                            )}
                            <p className="whitespace-pre-wrap break-words">{highlight(m.body)}</p>
                            {isDeleted && <p className="text-[10px] mt-1 opacity-80">(deleted)</p>}
                          </div>
                          <div className={`text-[10px] text-[#9a9aa6] mt-1 flex items-center gap-1.5 ${mine ? 'justify-end' : 'justify-start'}`}>
                            <span>{timeLabel(m.created_at)}</span>
                            {mine && !isDeleted && (
                              readByCounterpart
                                ? <CheckCheck className="w-3.5 h-3.5 text-[#0ea5e9]" data-testid={`read-${m.message_id}`} />
                                : <Check className="w-3.5 h-3.5" data-testid={`sent-${m.message_id}`} />
                            )}
                            {!isDeleted && (
                              <button
                                onClick={() => deleteMessage(m.message_id)}
                                className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-600 transition"
                                data-testid={`msg-del-${m.message_id}`}
                                title="Delete message"
                              >
                                <Trash2 className="w-3 h-3" />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </React.Fragment>
                  );
                })}
                {typingName && (
                  <div className="flex justify-start" data-testid="typing-indicator">
                    <div className="bg-white border border-[#E8E5DF] rounded-2xl rounded-bl-sm px-4 py-3 shadow-sm flex items-center gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#9a9aa6] animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="w-1.5 h-1.5 rounded-full bg-[#9a9aa6] animate-bounce" style={{ animationDelay: '150ms' }} />
                      <span className="w-1.5 h-1.5 rounded-full bg-[#9a9aa6] animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                  </div>
                )}
              </div>

              {/* Composer */}
              <div className="p-3 border-t border-[#E8E5DF] bg-white">
                <div className="flex items-end gap-2">
                  <Textarea
                    rows={2}
                    value={reply}
                    onChange={(e) => onReplyChange(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendReply(); } }}
                    placeholder={`Message ${selected.name}… (Enter to send, Shift+Enter for new line)`}
                    className="flex-1 rounded-xl border-[#E8E5DF] resize-none text-sm"
                    maxLength={2000}
                    data-testid="reply-input"
                  />
                  <div className="flex flex-col gap-1">
                    <button
                      onClick={() => setHighPriority((v) => !v)}
                      title="High priority"
                      className={`w-9 h-9 rounded-full flex items-center justify-center ${highPriority ? 'bg-red-500 text-white' : 'border border-[#E8E5DF] text-[#6B6B7A] hover:text-red-500'}`}
                      data-testid="toggle-high-priority"
                    >
                      <AlertTriangle className="w-4 h-4" />
                    </button>
                    <Button onClick={sendReply} disabled={sending || !reply.trim()} className="w-9 h-9 p-0 rounded-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white" data-testid="send-reply">
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                <p className="text-[10px] text-[#9a9aa6] mt-1">{reply.length}/2000</p>
              </div>
            </>
          )}
        </div>
      </div>

      {/* New message people picker */}
      <Dialog open={newMsgOpen} onOpenChange={(v) => { setNewMsgOpen(v); if (!v) setNewMsgSearch(''); }}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Fredoka' }}>New message</DialogTitle></DialogHeader>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9a9aa6]" />
            <Input autoFocus value={newMsgSearch} onChange={(e) => setNewMsgSearch(e.target.value)} placeholder="Search people by name…"
              className="pl-9 rounded-xl border-[#E8E5DF]" data-testid="new-message-search" />
          </div>
          <div className="max-h-[50vh] overflow-y-auto -mx-2 divide-y divide-[#F0EEEA]">
            {newMsgPeople.length === 0 ? (
              <p className="p-6 text-center text-[#6B6B7A] text-sm">No people found.</p>
            ) : newMsgPeople.map((p) => (
              <button
                key={p.user_id}
                onClick={() => startConversation(p)}
                className="w-full px-3 py-2.5 text-left hover:bg-[#FFFDF8] transition-colors flex items-center gap-3"
                data-testid={`new-msg-person-${p.user_id}`}
              >
                <div className="w-9 h-9 rounded-full bg-[#FF3B6B]/15 text-[#FF3B6B] flex items-center justify-center font-semibold flex-shrink-0">
                  {(p.name || 'E').slice(0, 1).toUpperCase()}
                </div>
                <div className="min-w-0">
                  <p className="font-medium text-[#1E1E24] truncate">{p.name || p.email}</p>
                  <p className="text-xs text-[#9a9aa6] truncate">{roleFor(p)}{p.email ? ` · ${p.email}` : ''}</p>
                </div>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete conversation confirm */}
      <Dialog open={deleteConvOpen} onOpenChange={setDeleteConvOpen}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Fredoka' }} className="text-red-600">Delete conversation?</DialogTitle></DialogHeader>
          <p className="text-sm text-[#6B6B7A]">All messages with <strong>{selected?.name}</strong> will be hidden from your view. You can bring them back with “Show deleted”.</p>
          <div className="flex gap-2 pt-2">
            <Button variant="outline" onClick={() => setDeleteConvOpen(false)} className="flex-1 rounded-full">Cancel</Button>
            <Button onClick={deleteConversation} className="flex-1 bg-red-500 hover:bg-red-600 text-white rounded-full" data-testid="confirm-delete-conv">Delete</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Broadcast composer (admin only) */}
      {cfg.allowBroadcast && (
        <Dialog open={broadcastOpen} onOpenChange={setBroadcastOpen}>
          <DialogContent className="max-w-lg bg-white">
            <DialogHeader><DialogTitle style={{ fontFamily: 'Fredoka' }}>New Broadcast</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-[#6B6B7A]">Recipients</label>
                <select
                  value={broadcastForm.scope}
                  onChange={(e) => setBroadcastForm((p) => ({ ...p, scope: e.target.value }))}
                  className="mt-1 w-full h-10 rounded-xl border border-[#E8E5DF] px-3 bg-white text-sm"
                  data-testid="broadcast-scope"
                >
                  <option value="all">All employees</option>
                  <option value="role">Filter by role</option>
                </select>
              </div>
              {broadcastForm.scope === 'role' && (
                <div>
                  <label className="text-xs text-[#6B6B7A]">Role</label>
                  <select value={broadcastForm.role} onChange={(e) => setBroadcastForm((p) => ({ ...p, role: e.target.value }))}
                    className="mt-1 w-full h-10 rounded-xl border border-[#E8E5DF] px-3 bg-white text-sm">
                    <option value="party_host">Party Host</option>
                    <option value="manager">Manager</option>
                    <option value="staff">Staff</option>
                  </select>
                </div>
              )}
              <div>
                <label className="text-xs text-[#6B6B7A]">Subject</label>
                <Input value={broadcastForm.subject} maxLength={100} onChange={(e) => setBroadcastForm((p) => ({ ...p, subject: e.target.value }))} className="mt-1 rounded-xl border-[#E8E5DF]" data-testid="broadcast-subject" />
              </div>
              <div>
                <label className="text-xs text-[#6B6B7A]">Message</label>
                <Textarea value={broadcastForm.body} rows={5} maxLength={2000} onChange={(e) => setBroadcastForm((p) => ({ ...p, body: e.target.value }))} className="mt-1 rounded-xl border-[#E8E5DF]" data-testid="broadcast-body" />
                <p className="text-[10px] text-[#9a9aa6] mt-1">
                  Tip: use <code className="bg-[#FFFDF8] px-1 rounded">@All</code>, <code className="bg-[#FFFDF8] px-1 rounded">@PartyHosts</code>, <code className="bg-[#FFFDF8] px-1 rounded">@Managers</code>, or <code className="bg-[#FFFDF8] px-1 rounded">@Staff</code> to include additional groups.
                </p>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={broadcastForm.high_priority} onChange={(e) => setBroadcastForm((p) => ({ ...p, high_priority: e.target.checked }))} data-testid="broadcast-priority" />
                High priority
              </label>
              <Button onClick={sendBroadcast} className="w-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white rounded-full" data-testid="send-broadcast">
                <Send className="w-4 h-4 mr-2" /> Send
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default TeamChat;
